<template>
  <section class="mt-2 space-y-5 px-14 sm-max:px-5 sm:space-y-12" id="about">
    <h2 v-if="showTitle" class="pb-10 text-center lg:text-42 md:text-32 text-22 leading-head md-max:pb-4 sm-max:text-21">
      <strong v-html="title"></strong>
    </h2>

    
    <div v-for="(section, index) in sections" :key="index" class="border-2 bg-greenBg border-greenD" :class="index == 0 ?'!mt-0' :''">
      <div class="grid grid-cols-8 md-only:px-10 sm-max:px-4">
        <div class="col-span-3 z-[1] md-max:col-span-8 sm-max:p-4">
          <nuxt-img
            class="m-auto md-max:mt-2 lg:-mt-2 lg:-ml-2 lg:-mb-2"
            :width="section.imageWidth"
            :height="section.imageHeight"
            loading="lazy"
            format="webp"
            :alt="section.imageAlt"
            :src="section.imageSrc"
          ></nuxt-img>
        </div>

        <div class="col-span-5 text-left md-max:col-span-8 md-max:text-center lg-max:px-6">
          <div
            class="inline-block py-2 pr-6 text-white bg-greenD lg:text-32 md:text-2xl text-15 sm-max:w-full sm-max:px-6 pl-14 sm:py-4 lg:mt-10 lg:-ml-12"
            v-html="section.title"
          ></div>

          <div
            class="pt-1 pb-10 mt-4 text-base leading-7 text-left lg:pl-2 lg:text-21 md:text-lg md-max:pb-10 md-max:text-center lg:pr-16"
          >
            <div class="leading-6 md:leading-30" v-html="section.description"></div>
          </div>
        </div>
      </div>

      <div class="mx-12 mb-10 bg-white border-2 border-greenD sm:mt-7 sm-max:mx-4" v-if="section.extraSection">
        <div class="grid items-center px-4 pt-6 pb-12 md-max:px-3 md:grid-cols-12 lg:pr-14">
          <div class="col-span-4 md-max:col-span-12">
            <nuxt-img
              class="relative w-32 m-auto md-max:mt-0 -top-2 sm:w-52"
              width="211"
              height="201"
              loading="lazy"
              format="webp"
              :alt="section.extraSectionImageAlt"
              :src="section.extraSectionImage"
            ></nuxt-img>
          </div>

          <div class="col-span-8 md-max:col-span-12 lg:-ml-0">
            <div class="text-base lg:text-21 md:text-lg md-max:text-center md-max:pt-2 lg:pt-2 md:leading-8">
              <div class="md-max:leading-5" v-html="section.extraSectionDescription"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  title: {
    type: String,
    default: 'Here’s Just a Few of <br><br> The Qualities of This Coffee:',
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
  sections: {
    type: Array,
    default: () => [
      {
        imageAlt: 'PURE INGREDIENT',
        imageSrc: '/assets/images/zoom-in-ingredients.jpg',
        imageWidth: 431,
        imageHeight: 339,
        title: 'ONE PURE INGREDIENT',
        description: 'Coffee is delicious, but poor quality coffee can be toxic. You want coffee that’s <strong>clean and pure to ensure there are never any pesticides…</strong>',
        extraSection: false,
        extraSectionImage: '',
        extraSectionImageAlt: '',
        extraSectionDescription: ''
      },
      {
        imageAlt: 'SINGLE ORIGIN',
        imageSrc: '/assets/images/freebag-sect3-img2.png',
        imageWidth: 408,
        imageHeight: 389,
        title: 'SINGLE ORIGIN',
        description: 'Most of the coffee for sale in retail stores and coffee shop is a mix-match of beans from various plants and countries around the world. With blends, you might get beans mixed with pesticide-soaked beans. It’s also possible you can get premium dark roast beans mixed with under-dried, toxin-filled lighter roast beans.<br><br>Single Origin coffee ensures you’re getting what you pay for - and nothing you don’t want in your coffee. <strong>Every bag of this coffee, however, is guaranteed single origin.</strong> Each bag is filled with beans from the same farm, the same plant, and the same roast profile.',
        extraSection: false,
        extraSectionImage: '',
        extraSectionImageAlt: '',
        extraSectionDescription: ''
      },
      {
        imageAlt: 'TESTED FOR MYCOTOXINS',
        imageSrc: '/assets/images/freebag-sect3-img3.png',
        imageWidth: 389,
        imageHeight: 370,
        title: 'TESTED FOR MYCOTOXINS',
        description: 'Mycotoxins are mold toxins that can occur in many substances. Naturally, that means <strong>they can grow inside coffee beans too.</strong> (Unfortunately, they’re far more common than you may think.) Mycotoxins can trigger anything from painful, full-body-inflammation to debilitating Chronic Fatigue Syndrome. Several studies have also strongly linked mycotoxins with kidney diseases.',
        extraSection: false,
        extraSectionImage: '',
        extraSectionImageAlt: '',
        extraSectionDescription: ''
      },
      {
        imageAlt: 'SHADE GROWN',
        imageSrc: '/assets/images/freebag-sect3-img4.png',
        imageWidth: 389,
        imageHeight: 370,
        title: 'SHADE GROWN',
        description: 'For the best-tasting coffee, you don’t want the sun beating down on the beans harming the natural maturation and rich, complex flavor. Only <strong>2% of the world’s coffee is shade-grown.</strong> With shade-grown coffee, no pesticides are needed. You deserve natural, unharmed, pure coffee.',
        extraSection: false,
        extraSectionImage: '',
        extraSectionImageAlt: '',
        extraSectionDescription: ''
      },
      {
        imageAlt: 'STOMACH-FRIENDLY',
        imageSrc: '/assets/images/freebag-sect3-img5.png',
        imageWidth: 389,
        imageHeight: 370,
        title: 'STOMACH-FRIENDLY',
        description: 'One of the biggest complaints about coffee is the digestive discomfort from some brews. You probably know all about it yourself… the heartburn, indigestion, bathroom trips, and more. <strong>Sometimes you just don’t know what a given coffee is going to do to your stomach!</strong><br><br> Now, a lot of that discomfort has to do with the acid in your stomach. And you may also know the discomfort depends on the type of roast you drink. <strong>(Hint: Medium and Dark Roasts are easier on the stomach!)</strong> So it can be a gamble as to how your stomach will handle a new coffee.<br><br> But because of the steps you’ve just read about above, Lifeboost Coffee guarantees its coffee won’t bother your stomach! There’s nothing inside that can trigger excess stomach acid or affect digestion.<br><br> <strong>Light, medium, dark or espresso, you choose your beans and your stomach is happy.</strong>',
        extraSection: true,
        extraSectionImage: '/assets/images/freebag-sect3-4logos.png',
        extraSectionImageAlt: 'Additional Stomach-Friendly Image',
        extraSectionDescription: 'On top of low acidity, Lifeboost Coffee has none of the junk swimming around in most coffees that aggravates your stomach. That\'s why we test for <strong> pesticides, chemicals, GMOs, or mycotoxins.</strong> <br><br> That means nothing that careless farmers and profit-hungry manufacturers use to get their brews out the door fast.'
      },
      {
        imageAlt: 'SHADE GROWN',
        imageSrc: '/assets/images/freebag-sect3-img1.png',
        imageWidth: 389,
        imageHeight: 370,
        title: 'PURE, CLEAN INGREDIENTS',
        description: 'Our coffee contains only one, simple, pure ingredient. You’re getting the cleanest coffee available directly from the highest quality farms located in a nationally-protected area of Central America. The coffee is then carefully roasted according to exact specifications to produce delicious, low-acid coffee you’ll love.',
        extraSection: false,
        extraSectionImage: '',
        extraSectionImageAlt: '',
        extraSectionDescription: ''
      }
    ],
  },
})

defineBuilderComponent({
  friendlyName: "Qualities",
  canHaveChildren: false,
  inputs: [
    {
      name: "title",
      type: "richText",
      defaultValue: "Here’s Just a Few of <br> The Qualities of This Coffee:"
    },
    {
      name: "showTitle",
      type: "boolean",
      defaultValue: true
    },
    {
      name: "sections",
      type: "list",
      defaultValue: [
        {
          imageAlt: 'PURE INGREDIENT',
          imageSrc: '/assets/images/zoom-in-ingredients.jpg',
          imageWidth: 431,
          imageHeight: 339,
          title: 'ONE PURE INGREDIENT',
          description: 'Coffee is delicious, but poor quality coffee can be toxic. You want coffee that’s <strong>clean and pure to ensure there are never any pesticides…</strong>',
          extraSection: false,
          extraSectionImage: '',
          extraSectionImageAlt: '',
          extraSectionDescription: ''
        },
        {
          imageAlt: 'SINGLE ORIGIN',
          imageSrc: '/assets/images/freebag-sect3-img2.png',
          imageWidth: 408,
          imageHeight: 389,
          title: 'SINGLE ORIGIN',
          description: 'Most of the coffee for sale in retail stores and coffee shop is a mix-match of beans from various plants and countries around the world. With blends, you might get beans mixed with pesticide-soaked beans. It’s also possible you can get premium dark roast beans mixed with under-dried, toxin-filled lighter roast beans.<br><br>Single Origin coffee ensures you’re getting what you pay for - and nothing you don’t want in your coffee. <strong>Every bag of this coffee, however, is guaranteed single origin.</strong> Each bag is filled with beans from the same farm, the same plant, and the same roast profile.',
          extraSection: false,
          extraSectionImage: '',
          extraSectionImageAlt: '',
          extraSectionDescription: ''
        },
        {
          imageAlt: 'TESTED FOR MYCOTOXINS',
          imageSrc: '/assets/images/freebag-sect3-img3.png',
          imageWidth: 389,
          imageHeight: 370,
          title: 'TESTED FOR MYCOTOXINS',
          description: 'Mycotoxins are mold toxins that can occur in many substances. Naturally, that means <strong>they can grow inside coffee beans too.</strong> (Unfortunately, they’re far more common than you may think.) Mycotoxins can trigger anything from painful, full-body-inflammation to debilitating Chronic Fatigue Syndrome. Several studies have also strongly linked mycotoxins with kidney diseases.',
          extraSection: false,
          extraSectionImage: '',
          extraSectionImageAlt: '',
          extraSectionDescription: ''
        },
        {
          imageAlt: 'SHADE GROWN',
          imageSrc: '/assets/images/freebag-sect3-img4.png',
          imageWidth: 389,
          imageHeight: 370,
          title: 'SHADE GROWN',
          description: 'For the best-tasting coffee, you don’t want the sun beating down on the beans harming the natural maturation and rich, complex flavor. Only <strong>2% of the world’s coffee is shade-grown.</strong> With shade-grown coffee, no pesticides are needed. You deserve natural, unharmed, pure coffee.',
          extraSection: false,
          extraSectionImage: '',
          extraSectionImageAlt: '',
          extraSectionDescription: ''
        },
        {
          imageAlt: 'STOMACH-FRIENDLY',
          imageSrc: '/assets/images/freebag-sect3-img5.png',
          imageWidth: 389,
          imageHeight: 370,
          title: 'STOMACH-FRIENDLY',
          description: 'One of the biggest complaints about coffee is the digestive discomfort from some brews. You probably know all about it yourself… the heartburn, indigestion, bathroom trips, and more. <strong>Sometimes you just don’t know what a given coffee is going to do to your stomach!</strong><br><br> Now, a lot of that discomfort has to do with the acid in your stomach. And you may also know the discomfort depends on the type of roast you drink. <strong>(Hint: Medium and Dark Roasts are easier on the stomach!)</strong> So it can be a gamble as to how your stomach will handle a new coffee.<br><br> But because of the steps you’ve just read about above, Lifeboost Coffee guarantees its coffee won’t bother your stomach! There’s nothing inside that can trigger excess stomach acid or affect digestion.<br><br> <strong>Light, medium, dark or espresso, you choose your beans and your stomach is happy.</strong>',
          extraSection: true,
          extraSectionImage: '/assets/images/freebag-sect3-4logos.png',
          extraSectionImageAlt: 'Additional Stomach-Friendly Image',
          extraSectionDescription: 'On top of low acidity, Lifeboost Coffee has none of the junk swimming around in most coffees that aggravates your stomach. That\'s why we test for <strong> pesticides, chemicals, GMOs, or mycotoxins.</strong> <br><br> That means nothing that careless farmers and profit-hungry manufacturers use to get their brews out the door fast.'
        },
        {
          imageAlt: 'SHADE GROWN',
          imageSrc: '/assets/images/freebag-sect3-img1.png',
          imageWidth: 389,
          imageHeight: 370,
          title: 'PURE, CLEAN INGREDIENTS',
          description: 'Our coffee contains only one, simple, pure ingredient. You’re getting the cleanest coffee available directly from the highest quality farms located in a nationally-protected area of Central America. The coffee is then carefully roasted according to exact specifications to produce delicious, low-acid coffee you’ll love.',
          extraSection: false,
          extraSectionImage: '',
          extraSectionImageAlt: '',
          extraSectionDescription: ''
        }
      ],
      subFields: [
        {
          name: "imageAlt",
          type: "text",
          defaultValue: "Image Alt"
        },
        {
          name: "imageSrc",
          type: "file",
          allowedFileTypes: [
            "jpeg",
            "jpg",
            "png",
            "svg"
          ],
          defaultValue: "/assets/images/default.jpg"
        },
        {
          name: "imageWidth",
          type: "number",
          defaultValue: 431
        },
        {
          name: "imageHeight",
          type: "number",
          defaultValue: 339
        },
        {
          name: "title",
          type: "richText",
          defaultValue: "Section Title"
        },
        {
          name: "description",
          type: "richText",
          defaultValue: "Section Description"
        },
        {
          name: "extraSection",
          type: "boolean",
          defaultValue: false
        },
        {
          name: "extraSectionImage",
          type: "file",
          allowedFileTypes: [
            "jpeg",
            "jpg",
            "png",
            "svg"
          ],
          defaultValue: "/assets/images/freebag-sect3-4logos.png"
        },
        {
          name: "extraSectionImageAlt",
          type: "text",
          defaultValue: "Extra Section Image Alt"
        },
        {
          name: "extraSectionDescription",
          type: "richText",
          defaultValue: "Extra Section Description"
        },
      ]
    }
  ]
});
</script>